import * as React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat';

import { SkillCategoryInterface, StudentPerformanceData, ClassroomInterface, StateStandardInterface, } from '../interfaces';
import { alphabeticalName, } from '../../../../../../modules/sortingMethods'
import {
  DataTable,
  DataTableChip,
  Spinner,
  Tooltip,
  accountGreenIcon,
  helpIcon,
} from '../../../../../Shared/index';
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths';
import { useStickyTableHeader } from '../../../../../Shared/hooks/useStickyTableHeader';
import { NONE, smartGroupToClassName, getSortableSmartGroup, formatTime, formatMomentumScore, formatTrend, formatPercentage, formatLastPracticed, getSortableMomentumScore, getSortableTrend, getSortablePercentage, getSortableTime, getSortableLastPracticed, getSortableExposure, getSmartGroup, extraSmallLockIcon, } from '../../shared/shared';
import { NoStudentsEmptyState, classroomHasNoStudents, } from '../shared';

const helpIconImg = <img alt={helpIcon.alt} src={helpIcon.src} />

interface ByStudentProps {
  classroom: ClassroomInterface;
  containerRef: React.RefObject<HTMLElement>;
  momentumScoreGoal: number;
  selectedSkillCategory: SkillCategoryInterface;
  selectedStateStandard: StateStandardInterface | null;
  data: StudentPerformanceData[] | null;
  onStudentClick?: (studentId: number | string, studentName: string) => void;
}

const ByStudent: React.FC<ByStudentProps> = ({
  classroom,
  containerRef,
  data,
  momentumScoreGoal, 
  onStudentClick,
  selectedStateStandard,
}) => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = React.useState<(number)[]>([])
  
  const tableRef = React.useRef<HTMLTableElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setSelectedRows([])
  }, [data])

  const columnWidths = [
    { width: 300, minWidth: 170 }, // Student Name
    { width: 200, minWidth: 140 }, // Smart Group
    { width: 200, minWidth: 110 }, // Momentum Score
    { width: 300, minWidth: 140 }, // Momentum Score Trend
    { width: 200, minWidth: 116 }, // Exposure
    { width: 200, minWidth: 116 }, // Questions Answered
    { width: 200, minWidth: 116 }, // Replay Rate
    { width: 200, minWidth: 116 }, // Time Spent
    { width: 200, minWidth: 100 }, // Last Practiced
  ];

  const scaledWidths = useScaledTableColumnWidths({
    columnWidths,
    containerRef,
    maxTableWidth: 2000,
    minTableWidth: 1200,
    additionalWidthRestrictions: 99
  });

  useStickyTableHeader({
    wrapperRef,
    tableRef,
    enabled: true,
    dependencies: [data],
  });


  const handleStudentClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const studentId = event.currentTarget.dataset.studentId;
    const studentName = event.currentTarget.dataset.studentName;
    if (studentId && studentName && onStudentClick) {
      onStudentClick(studentId, studentName);
    }
  }, [onStudentClick]);

  const handleViewAllUnpracticedStandards = () => {
    navigate(`/teachers/progress_reports/standards_report/classroom/${classroom.id}/performance_breakdown/by_standard`);
  };

  const headers = [
    {
      width: scaledWidths['300w-170min'],
      name: 'Student Name',
      attribute: 'name',
      sortAttribute: 'nameForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-140min'],
      ariaLabel: 'Smart Group',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Smart Group</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={`Dynamic groups based on how this student's performance for this standard compares against your goal of ${momentumScoreGoal}. These groups also consider the student's exposure to ensure the grouping is reliable. You can change this goal at any time by clicking the 'Change goal' button.`}
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'smartGroup',
      sortAttribute: 'smartGroupForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-110min'],
      ariaLabel: 'Momentum Score',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Momentum Score</span>
          <Tooltip
            skipPositioning={true}
            tooltipText="A dynamic score that reflects a student's current understanding by giving more weight to their recent practice and rewarding accuracy."
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'momentumScore',
      sortAttribute: 'momentumScoreForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score'
    },
    {
      width: scaledWidths['300w-140min'],
      ariaLabel: 'Momentum Score Trend',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Momentum Score Trend</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={`Analyzes recent changes in this student's Momentum Score to reveal their performance trajectory for this standard.`}
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'trend',
      sortAttribute: 'trendForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score-trend'
    },
    {
      width: scaledWidths['200w-116min'],
      ariaLabel: 'Exposure',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Exposure</span>
          <Tooltip
            skipPositioning={true}
            tooltipText='A measure of the practice this student has completed for this standard. Higher exposure increases the reliability of the Momentum Score.'
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'exposure',
      sortAttribute: 'exposureForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-116min'],
      ariaLabel: 'Questions Answered',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Questions Answered</span>
          <Tooltip
            skipPositioning={true}
            tooltipText='The total number of questions this student has answered for this standard.'
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'totalAnswered',
      sortAttribute: 'totalAnsweredForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-116min'],
      ariaLabel: 'Replay Rate',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Replay Rate</span>
          <Tooltip
            skipPositioning={true}
            tooltipText="The percentage of a student's total answers that are from replayed questions. A high rate with strong performance demonstrates persistence, while a high rate with poor performance signals a need for support."
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'replayRate',
      sortAttribute: 'replayRateForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-116min'],
      ariaLabel: 'Time Spent',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Time Spent</span>
          <Tooltip
            skipPositioning={true}
            tooltipText='The total time this student has spent answering questions for this standard.'
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'timespent',
      sortAttribute: 'timespentForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-100min'],
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Last Practiced</span>
          <Tooltip
            skipPositioning={true}
            tooltipText="The most recent date this student answered a question for this standard."
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'lastPracticed',
      sortAttribute: 'lastPracticedForSort',
      isSortable: true
    },
  ];

  if (classroomHasNoStudents(classroom)) {
    return <NoStudentsEmptyState />
  }

  if (!selectedStateStandard) {
    return (
      <div className='empty-state'>
        <h2>
          No standards aligned to this grade level have been practiced yet
        </h2>
        <button 
          className="quill-button contained medium green focus-on-light"
          onClick={handleViewAllUnpracticedStandards}
          type="button"
        >
          View unpracticed standards in Performance Breakdown
        </button>
      </div>
    )
  }


  if (!data) { return <Spinner /> }

  const rows = data.map(row => {
    const { id, name, ema, trend, ema_previous, total_answered, exposure, replay_rate, last_practiced, timespent, } = row
    const smartGroup = getSmartGroup(exposure, ema, total_answered, momentumScoreGoal)
    return {
      id,
      name: (
        <button 
          className="interactive-wrapper focus-on-light" 
          data-student-id={id}
          data-student-name={name}
          onClick={handleStudentClick}
          type="button"
        >
          <DataTableChip color="green" icon={accountGreenIcon} label={name} />
        </button>
      ),
      nameForSort: alphabeticalName(name),
      momentumScore: formatMomentumScore(ema, exposure),
      momentumScoreForSort: getSortableMomentumScore(ema),
      trend: formatTrend(trend, total_answered),
      trendForSort: getSortableTrend(trend),
      exposure: total_answered === 0 ? NONE : exposure,
      exposureForSort: getSortableExposure(total_answered === 0 ? NONE : exposure),
      totalAnswered: total_answered || '—',
      totalAnsweredForSort: total_answered || 0,
      replayRate: formatPercentage(replay_rate, total_answered),
      replayRateForSort: getSortablePercentage(replay_rate, total_answered),
      lastPracticed: formatLastPracticed(last_practiced),
      lastPracticedForSort: getSortableLastPracticed(last_practiced),
      smartGroup: <span className={smartGroupToClassName[smartGroup]}>{smartGroup}</span>,
      timespent: formatTime(timespent),
      timespentForSort: getSortableTime(timespent),
      smartGroupForSort: getSortableSmartGroup(smartGroup, ema, alphabeticalName(name))
    }
  });

  return (
    <div className="by-student">
      <div className="data-table-wrapper" ref={wrapperRef}>
        <DataTable
          defaultSortAttribute='smartGroupForSort'
          headers={headers}
          reportingFormat={true}
          rows={rows}
          tableRef={tableRef}
        />
      </div>
    </div>
  );
}

export default ByStudent
